/* eslint-disable react/prop-types */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Modal } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import ActionsSearchField from "./ActionsSearchField";

const CENTERED_MODAL_WIDTH = 440;
/** Even inset around the search so it matches the top-bar field size */
const ANCHOR_INSET = 12;
const VIEWPORT_MARGIN = 8;
const EXIT_MS = 200;

const readAnchorRect = (anchorRef) => {
    const el = anchorRef?.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        right: rect.right,
        bottom: rect.bottom,
    };
};

const clampPosition = (top, left, width) => ({
    top: Math.max(VIEWPORT_MARGIN, top),
    left: Math.max(VIEWPORT_MARGIN, Math.min(left, window.innerWidth - width - VIEWPORT_MARGIN)),
});

const layoutFromAnchor = (anchorRect) => {
    const width = anchorRect.width + ANCHOR_INSET * 2;
    return {
        width,
        ...clampPosition(anchorRect.top - ANCHOR_INSET, anchorRect.left - ANCHOR_INSET, width),
    };
};

const AllActionsModalContent = ({
    t,
    searchTerm,
    setSearchTerm,
    filteredGroups,
    handleItemClick,
    onClose,
    searchFieldRef,
    inputRef,
    searchFirst = false,
    searchClassName = "",
}) => {
    const padX = searchFirst ? "px-3" : "px-6";

    const header = (
        <div className={`flex items-center justify-between ${padX} ${searchFirst ? "pb-2 pt-3" : "pt-6"}`}>
            <h2 className={`font-bold text-gray-900 ${searchFirst ? "text-base" : "text-xl"}`}>{t("allActions")}</h2>
            <button
                type="button"
                onClick={onClose}
                aria-label={t("close")}
                className="flex h-7 w-7 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800">
                <CloseOutlined />
            </button>
        </div>
    );

    const search = (
        <div className={searchFirst ? "px-3 pt-3" : "px-6 pb-4 pt-4"}>
            <ActionsSearchField
                ref={searchFieldRef}
                inputRef={inputRef}
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder={t("searchActionsPlaceholder")}
                className={searchClassName}
            />
        </div>
    );

    return (
        <>
            {searchFirst ? (
                <>
                    {search}
                    {header}
                </>
            ) : (
                <>
                    {header}
                    {search}
                </>
            )}

            <div className="border-t border-gray-100" />

            <div className={`max-h-[420px] overflow-y-auto pb-5 ${padX}`}>
                {filteredGroups.length === 0 ? (
                    <p className="py-8 text-center text-sm text-gray-400">{t("noActionsFound")}</p>
                ) : (
                    filteredGroups.map((group) => (
                        <div key={group.key} className="mt-4 first:mt-3">
                            <p className="mb-1 px-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
                                {group.label}
                            </p>
                            <div>
                                {group.items.map((item) => (
                                    <button
                                        key={item.key}
                                        type="button"
                                        onClick={() => handleItemClick(item.onClick)}
                                        className="flex w-full items-start gap-2.5 rounded-xl px-1 py-2 text-left transition-colors hover:bg-gray-50">
                                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#F0F0FE] text-sm text-[#5D5DF6]">
                                            {item.icon}
                                        </span>
                                        <span className="min-w-0">
                                            <span className="block text-sm font-semibold text-gray-900">
                                                {item.label}
                                            </span>
                                            <span className="block truncate text-xs text-gray-500">
                                                {item.description}
                                            </span>
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </>
    );
};

const AllActionsModal = ({ open, onClose, actionGroups, initialSearchTerm = "", anchorRef }) => {
    const { t } = useTranslation("dashboard");
    const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
    const [anchorRect, setAnchorRect] = useState(null);
    const [layout, setLayout] = useState(null);
    const [rendered, setRendered] = useState(false);
    const [active, setActive] = useState(false);
    const panelRef = useRef(null);
    const searchFieldRef = useRef(null);
    const inputRef = useRef(null);
    const exitTimerRef = useRef(null);

    const isAnchored = Boolean(anchorRef);

    const measureAndPosition = useCallback(() => {
        const nextRect = readAnchorRect(anchorRef);
        if (!nextRect) return null;
        setAnchorRect(nextRect);
        setLayout(layoutFromAnchor(nextRect));
        return nextRect;
    }, [anchorRef]);

    useEffect(() => {
        if (open) {
            setSearchTerm(initialSearchTerm);
        } else if (!rendered) {
            setSearchTerm("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!isAnchored) return undefined;

        if (open) {
            if (exitTimerRef.current) {
                clearTimeout(exitTimerRef.current);
                exitTimerRef.current = null;
            }
            setRendered(true);
            return undefined;
        }

        setActive(false);
        exitTimerRef.current = setTimeout(() => {
            setRendered(false);
            setAnchorRect(null);
            setLayout(null);
            setSearchTerm("");
            exitTimerRef.current = null;
        }, EXIT_MS);

        return () => {
            if (exitTimerRef.current) {
                clearTimeout(exitTimerRef.current);
                exitTimerRef.current = null;
            }
        };
    }, [open, isAnchored]);

    useLayoutEffect(() => {
        if (!rendered || !isAnchored) return undefined;

        measureAndPosition();

        const handleReposition = () => measureAndPosition();
        window.addEventListener("resize", handleReposition);
        window.addEventListener("scroll", handleReposition, true);
        return () => {
            window.removeEventListener("resize", handleReposition);
            window.removeEventListener("scroll", handleReposition, true);
        };
    }, [rendered, isAnchored, measureAndPosition]);

    useEffect(() => {
        if (!isAnchored || !open || !rendered || !layout) return undefined;

        const frame = requestAnimationFrame(() => {
            requestAnimationFrame(() => setActive(true));
        });
        return () => cancelAnimationFrame(frame);
    }, [isAnchored, open, rendered, layout]);

    useEffect(() => {
        const shouldFocus = isAnchored ? active : open;
        if (!shouldFocus) return undefined;

        const focusInput = () => {
            inputRef.current?.focus({ preventScroll: true });
        };

        const frame = requestAnimationFrame(focusInput);
        const timeout = setTimeout(focusInput, 50);
        return () => {
            cancelAnimationFrame(frame);
            clearTimeout(timeout);
        };
    }, [isAnchored, active, open]);

    useEffect(() => {
        if (!open && !(isAnchored && rendered)) return undefined;
        const onKeyDown = (event) => {
            if (event.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [open, onClose, isAnchored, rendered]);

    const normalizedSearch = searchTerm.trim().toLowerCase();

    const filteredGroups = actionGroups
        .map((group) => ({
            ...group,
            items: group.items.filter(
                (item) =>
                    !normalizedSearch ||
                    item.label.toLowerCase().includes(normalizedSearch) ||
                    item.description.toLowerCase().includes(normalizedSearch),
            ),
        }))
        .filter((group) => group.items.length > 0);

    const handleItemClick = (onItemClick) => {
        onItemClick();
        onClose();
    };

    const contentProps = {
        t,
        searchTerm,
        setSearchTerm,
        filteredGroups,
        handleItemClick,
        onClose,
        searchFieldRef,
        inputRef,
        searchFirst: isAnchored,
        searchClassName: isAnchored ? "py-2" : "",
    };

    if (isAnchored) {
        if (!rendered || !layout || !anchorRect) return null;

        const originX = anchorRect.left + anchorRect.width / 2 - layout.left;
        const originY = anchorRect.top + anchorRect.height / 2 - layout.top;

        return createPortal(
            <div
                className={`fixed inset-0 z-[1100] ${active ? "pointer-events-auto" : "pointer-events-none"}`}
                role="dialog"
                aria-modal="true"
                aria-label={t("allActions")}>
                <button
                    type="button"
                    aria-label={t("close")}
                    className={`absolute inset-0 border-0 bg-black/45 transition-opacity duration-200 ease-out ${
                        active ? "opacity-100" : "opacity-0"
                    }`}
                    onClick={onClose}
                />
                <div
                    ref={panelRef}
                    className={`absolute overflow-hidden rounded-2xl bg-white shadow-2xl transition-[opacity,transform] duration-200 ease-out will-change-[opacity,transform] ${
                        active ? "translate-y-0 scale-100 opacity-100" : "-translate-y-1.5 scale-95 opacity-0"
                    }`}
                    style={{
                        top: layout.top,
                        left: layout.left,
                        width: layout.width,
                        transformOrigin: `${originX}px ${originY}px`,
                    }}>
                    <AllActionsModalContent {...contentProps} />
                </div>
            </div>,
            document.body,
        );
    }

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            closable={false}
            width={CENTERED_MODAL_WIDTH}
            centered
            styles={{
                content: { padding: 0, borderRadius: 20, overflow: "hidden" },
                body: { padding: 0 },
            }}>
            <AllActionsModalContent {...contentProps} />
        </Modal>
    );
};

export default AllActionsModal;
